import { Descriptions, Divider, Typography } from 'antd';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Worker, WorkerQualification } from 'app/types/worker';
import { formatDate } from 'utils/datetime';
import { documentTypeName } from 'utils/worker';
import { useScreenBreakpoint } from 'hooks';

type WorkerKey = keyof Worker;

type Group = {
  title: string;
  keys: WorkerKey[];
};

const topGroups: Group[] = [
  { title: 'contactinformation', keys: [ 'PhoneNumber', 'Email', 'AddressText' ] },
  { title: 'employment', keys: [ 'EmployerName', 'EmployerRegistrationCode', 'JobTitle', 'EmploymentStartDate' ] },
];

const notesGroup: Group = { title: 'notes', keys: [ 'AdditionalInfo', 'InternalInduction' ] };

const dateKeys: WorkerKey[] = [ 'EmploymentStartDate' ];

const isMaskFitTest = (qualification: WorkerQualification) => /^MASK_FIT_TEST/.test(qualification.Code ?? '');

const isHealthExamination = (qualification: WorkerQualification) => qualification.Code === 'HEALTH_EXAMINATION';

export const isExpired = (value?: Date | string | null) => !!value && moment(value).isBefore(moment(), 'day');

type DataProps = {
  worker: Worker;
};

const Data = ({ worker }: DataProps) => {
  const { t } = useTranslation();
  const { isMedium } = useScreenBreakpoint();

  const renderValue = (key: WorkerKey) => {
    const value = worker[key];

    if (value === undefined || value === null || value === '') return <Typography.Text strong>-</Typography.Text>;
    if (typeof value === 'boolean') return <Typography.Text strong>{value ? t('common.yes') : t('common.no')}</Typography.Text>;
    if (dateKeys.includes(key)) return <Typography.Text strong>{formatDate(value as Date)}</Typography.Text>;

    return <Typography.Text strong>{String(value)}</Typography.Text>;
  };

  // "valid from – valid until", red when the end has passed
  const renderRange = (from?: Date | null, to?: Date | null, expired = isExpired(to)) => {
    const fromText = from ? formatDate(from) : '';
    const toText = to ? formatDate(to) : '';
    const text = toText ? `${fromText ? `${fromText} – ` : ''}${toText}` : fromText || t('entities.workerdocument.novalidity');

    return <Typography.Text strong type={expired ? 'danger' : undefined}>{text}</Typography.Text>;
  };

  // Newest document of the type
  const renderQualification = (qualification: WorkerQualification) => {
    if (!qualification.DocumentId) return <Typography.Text strong>-</Typography.Text>;
    return renderRange(qualification.ValidFrom, qualification.ValidTo, qualification.IsExpired);
  };


  const renderGroup = (title: string, items: { key: string; label: string; value: JSX.Element }[]) => (
    <Fragment key={title}>
      <Divider orientation={isMedium ? 'left' : 'center'}>{t(`entities.worker.${title}`)}</Divider>
      <Descriptions column={1} size={'small'} layout={isMedium ? 'horizontal' : 'vertical'}>
        {items.map((item) => (
          <Descriptions.Item key={item.key} label={item.label}>{item.value}</Descriptions.Item>
        ))}
      </Descriptions>
    </Fragment>
  );

  const fieldItems = (keys: WorkerKey[]) =>
    keys.map((key) => ({ key, label: t(`entities.worker.${key.toLowerCase()}`), value: renderValue(key) }));

  const qualificationItems = (qualifications: WorkerQualification[]) =>
    qualifications.map((q) => ({ key: `type-${q.DocumentTypeId}`, label: documentTypeName(q), value: renderQualification(q) }));

  const qualifications = worker.Qualifications ?? [];

  const healthItems = [
    ...fieldItems([ 'IsHealthSuitableForAsbestosWork' ]),
    ...qualificationItems(qualifications.filter(isHealthExamination)),
  ];

  return (
    <>
      {topGroups.map((group) => renderGroup(group.title, fieldItems(group.keys)))}
      {renderGroup('qualifications', [
        ...qualificationItems(qualifications.filter((q) => !isMaskFitTest(q) && !isHealthExamination(q))),
        ...fieldItems([ 'IsInAsbestosRegister' ]),
      ])}
      {renderGroup('maskfittests', qualificationItems(qualifications.filter(isMaskFitTest)))}
      {renderGroup('health', healthItems)}
      {renderGroup(notesGroup.title, fieldItems(notesGroup.keys))}
    </>
  );
};

export default Data;
